// Test & Refine redesign - shared presentational helpers.
// Pure, stateless building blocks used across the redesign views:
// the AI orb, status/type/priority maps, small pills, and number
// formatters. Colors come from tokens only (see test-refine-redesign.css
// for the scoped token shim). No data is authored here.
import type { CSSProperties, ReactNode } from "react";
import { Sparkles } from "lucide-react";

export const ORB_GRADIENT = "var(--orb-gradient)";

// ── Number formatting ───────────────────────────────────────────
export const pct = (n: number | null | undefined): string =>
  n === null || n === undefined || Number.isNaN(Number(n)) ? "–" : `${Math.round(Number(n))}%`;

export const signedPp = (n: number | null | undefined): string => {
  const v = Math.round(Number(n ?? 0));
  return `${v >= 0 ? "+" : ""}${v}pp`;
};

export const clampPctWidth = (n: number): string => `${Math.max(0, Math.min(100, n))}%`;

// ── Priority ────────────────────────────────────────────────────
export type PriorityKey = "HIGH" | "MEDIUM" | "LOW";

export function priorityConfig(priority?: string): {
  label: string;
  bg: string;
  color: string;
} {
  const p = String(priority || "MEDIUM").toUpperCase();
  if (p === "HIGH") {
    return { label: "High", bg: "var(--infrared-coral-10)", color: "var(--infrared-coral)" };
  }
  if (p === "LOW") {
    return { label: "Low", bg: "var(--muted)", color: "var(--muted-foreground)" };
  }
  return { label: "Medium", bg: "var(--warning-bg)", color: "var(--warning-text)" };
}

// ── Effort ──────────────────────────────────────────────────────
export function effortColor(effort?: string): string {
  const e = String(effort || "Medium").toLowerCase();
  if (e === "low") return "var(--signal-green)";
  if (e === "high") return "var(--infrared-coral)";
  return "var(--signal-blue)";
}

// ── Type / theme accent ─────────────────────────────────────────
// The mock keyed accents off a fixed set of theme strings. In prod the
// queue item `type` is looser, so we map the known ones and fall back
// to muted. HIGH priority always overrides to coral (matches the DC).
const TYPE_COLORS: Record<string, string> = {
  "fee differentiation": "var(--signal-blue)",
  "core claims": "var(--infrared-coral)",
  "comparison content": "var(--spectral-violet)",
  "workflow clarity": "var(--signal-green)",
  "crawlable evidence": "var(--signal-blue)",
  "proof hub": "var(--spectral-violet)",
};

export function typeColor(type?: string): string {
  return TYPE_COLORS[String(type || "").toLowerCase()] || "var(--muted-foreground)";
}

export function rowAccent(priority?: string, type?: string): string {
  return String(priority || "").toUpperCase() === "HIGH" ? "var(--infrared-coral)" : typeColor(type);
}

// ── Discovery tag config ────────────────────────────────────────
export type DiscoveryTag = "positive" | "neutral" | "warning";

export function discoveryConfig(tag: DiscoveryTag): { color: string; bg: string; border: string } {
  if (tag === "positive")
    return { color: "var(--signal-green)", bg: "var(--signal-green-10)", border: "var(--signal-green-20)" };
  if (tag === "warning")
    return { color: "var(--infrared-coral)", bg: "var(--infrared-coral-10)", border: "var(--infrared-coral-20)" };
  return { color: "var(--signal-blue)", bg: "var(--signal-blue-10)", border: "var(--signal-blue-20)" };
}

// ── The AI orb ──────────────────────────────────────────────────
export function TrOrb({
  size = 36,
  radius = 10,
  iconSize,
  animate = true,
  style,
}: {
  size?: number;
  radius?: number;
  iconSize?: number;
  animate?: boolean;
  style?: CSSProperties;
}) {
  return (
    <span
      className={animate ? "tr-orb" : undefined}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: ORB_GRADIENT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--on-orb)",
        flexShrink: 0,
        ...style,
      }}
    >
      <Sparkles size={iconSize ?? Math.round(size * 0.5)} strokeWidth={1.7} />
    </span>
  );
}

// ── Small pill ──────────────────────────────────────────────────
export function Pill({
  children,
  color = "var(--muted-foreground)",
  bg = "var(--muted)",
  border,
  style,
}: {
  children: ReactNode;
  color?: string;
  bg?: string;
  border?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 9999,
        padding: "2px 9px",
        color,
        background: bg,
        border: border ? `1px solid ${border}` : undefined,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// Uppercase section eyebrow used to label groups ("The evidence").
export function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: ".07em",
        color: "var(--muted-foreground)",
        padding: "8px 2px 0",
      }}
    >
      {children}
    </div>
  );
}
